import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import SitePlannerViewer from '../Viewer/SitePlannerViewer';
import XlabsStageChrome from '../Xlabs/XlabsStageChrome';
import LocateToolRail from '../Xlabs/LocateToolRail';
import LocateSidePanel from '../Xlabs/LocateSidePanel';
import XlabsThemeViewPanel from '../Xlabs/XlabsThemeViewPanel';
import { useXlabsConfigurator } from '../../context/XlabsConfiguratorContext';
import useInternalTheme from '../../hooks/useInternalTheme';
import {
  insetPlan,
  polygonArea,
  toPlanVertices,
} from '../../utils/plotGeom';
import { formatFt, formatSqFt } from '../../utils/unitsUs';
import './XlabsLocate.css';

/**
 * xlabs Locate hybrid — xlabs chrome + Construx SitePlannerViewer.
 * See docs/XLABS_LOCATE.md
 */
export default function XlabsLocate() {
  const state = useXlabsConfigurator();
  const theme = useInternalTheme();
  const localizeRef = useRef(null);

  const planVerts = useMemo(
    () => toPlanVertices(state.lot?.vertices || []),
    [state.lot?.vertices],
  );
  const inset = useMemo(
    () => insetPlan(planVerts, state.setback),
    [planVerts, state.setback],
  );
  const area = useMemo(() => polygonArea(planVerts), [planVerts]);
  const buildArea = useMemo(() => polygonArea(inset), [inset]);

  const onBindLocalize = useCallback((fn) => {
    localizeRef.current = fn;
  }, []);

  const { setTool, setLotSelected, setSelectedVertexIndex } = state;

  const onToolChange = useCallback(
    (next) => {
      setTool(next);
      if (next === 'select' || next === 'draw') {
        setLotSelected(true);
      }
    },
    [setTool, setLotSelected],
  );

  // Design uses `place`; map it back so Locate rail stays valid.
  useEffect(() => {
    if (state.tool === 'place') setTool('select');
  }, [state.tool, setTool]);

  // Keep lot selected in edit tools so vertex handles stay visible.
  useEffect(() => {
    if (state.tool === 'select' || state.tool === 'draw') {
      setLotSelected(true);
    }
  }, [state.tool, setLotSelected]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const k = e.key.toLowerCase();
      if (k === 'v') onToolChange('select');
      else if (k === 'r') onToolChange('draw');
      else if (k === 'm') onToolChange('measure');
      else if (k === 'escape') {
        setLotSelected(false);
        setSelectedVertexIndex(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onToolChange, setLotSelected, setSelectedVertexIndex]);

  const hudLeft = [
    { k: 'tool', v: state.tool },
    {
      k: 'snap',
      v: state.snap.enabled ? formatFt(state.snap.size, 1) : 'off',
    },
    { k: 'verts', v: String(planVerts.length) },
  ];
  const hudRight = [
    { k: 'plot', v: formatSqFt(area) },
    { k: 'build envelope', v: formatSqFt(buildArea) },
    { k: 'setback', v: formatFt(state.setback) },
  ];

  const sideProps = {
    address: state.address,
    onAddressChange: state.setAddress,
    onLoadSite: state.loadSite,
    siteLoading: state.siteLoading,
    siteError: state.siteError,
    contextNote: state.contextNote,
    site: state.site,
    lot: state.lot,
    setback: state.setback,
    onSetbackChange: state.setSetback,
    northDeg: state.northDeg,
    onNorthChange: state.setNorthDeg,
    snap: state.snap,
    onSnapChange: (patch) => state.setSnap((prev) => ({ ...prev, ...patch })),
    selectedVertexIndex: state.selectedVertexIndex,
    onSelectVertex: (i) => {
      state.setSelectedVertexIndex(i);
      state.setLotSelected(true);
    },
    onApplyPreset: state.applyPreset,
    onResetLotFloor: state.resetLotFloor,
    floorY: state.floorY,
  };

  const layers = useMemo(
    () => ({ roof: true, walls: true, interior: true }),
    [],
  );
  const section = useMemo(() => ({ enabled: false, offset: 0 }), []);

  return (
    <div
      className="xlabs-stage"
      data-hub-theme={theme === 'light' ? undefined : theme}
    >
      <Helmet>
        <title>xlabs · locate</title>
        <meta
          name="description"
          content="xlabs Locate — plot polygon, setbacks, north heading on Construx site planner."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <XlabsThemeViewPanel />

      <XlabsStageChrome
        activeStage="locate"
        tool={state.tool}
        ToolRail={
          <LocateToolRail tool={state.tool} onToolChange={onToolChange} />
        }
        SidePanel={
          <LocateSidePanel {...sideProps} showAddressSearch={false} />
        }
        siteBooting={state.siteBooting}
        siteLoading={state.siteLoading}
        floorY={state.floorY}
        site={state.site}
        hudLeft={hudLeft}
        hudRight={hudRight}
        northDeg={state.northDeg}
        showAddressSearch
        bootName="xlabs · locate"
        statusHint={
          state.contextNote || 'ESC clears · V/R/M tools'
        }
        sideProps={sideProps}
      >
        <div className="locate-viewer">
          <SitePlannerViewer
            site={state.site}
            lot={state.lot}
            onLotChange={state.onLotChange}
            mapBuildings={state.mapBuildings}
            contextMode={state.contextMode}
            tileSnapshotLevel={state.tileSnapshotLevel}
            onTilesAttemptFailed={state.onTilesAttemptFailed}
            viewLocked={state.viewLocked}
            onViewLockedChange={state.setViewLocked}
            lotSelected={state.lotSelected}
            onLotSelectedChange={state.setLotSelected}
            floorY={state.floorY}
            onFloorLock={state.onFloorLock}
            onFloorDebug={state.onFloorDebug}
            floorDebug={state.floorDebug}
            structures={state.structures}
            selectedId={state.selectedId}
            onSelectStructure={state.setSelectedId}
            onUpdateStructure={state.updateStructure}
            layers={layers}
            section={section}
            onBindLocalize={onBindLocalize}
            setbackMeters={state.setback}
            northDeg={state.northDeg}
            selectedVertexIndex={state.selectedVertexIndex}
            onSelectVertex={(i) => {
              setSelectedVertexIndex(i);
              setLotSelected(true);
            }}
            className="!h-full"
            heightClass="h-full"
          />
        </div>
      </XlabsStageChrome>
    </div>
  );
}
